import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as HiIcons from 'react-icons/hi';
import * as RiIcons from 'react-icons/ri';
import { clsx } from 'clsx';
import exportManager from './ExportManager';

const { FiDownload, FiX, FiCheck, FiFileText, FiCamera, FiCopy, FiCpu, FiGrid, FiLayers, FiSun, FiBox, FiPenTool } = FiIcons;
const { HiOutlinePhotograph } = HiIcons;

const RENDER_STYLES = {
  photorealistic: {
    id: 'photorealistic',
    label: 'Fotorealistyczny Ultra 8K',
    icon: FiCamera,
    description: 'Najwyższa jakość wizualizacji z pełnym oświetleniem globalnym, fizycznymi materiałami i realistycznymi cieniami. Idealny do prezentacji finalnych projektów.',
    prompt: `Generate a photorealistic orthographic top-down 3D rendering based strictly on the provided 2D floor plan image.
Convert all walls, doors, windows, and architectural elements into accurate 3D geometry with correct thickness, proportions, and alignment.
Use a true orthographic camera (no perspective distortion), perfectly vertical top view.
Apply realistic global illumination, soft ambient occlusion, and subtle natural shadows.
Floors should use realistic physical materials (wood, ceramic tiles, concrete) depending on room function.
Walls should be matte white with gentle shading and light edge definition.
Furniture and fixtures must match the exact shapes, sizes, and positions from the 2D plan.
Style: ultra-realistic architectural visualization, PBR materials, clean geometry, high realism, 8K quality.`
  },
  isometric_3d: {
    id: 'isometric_3d',
    label: 'Rzut 3D pod kątem',
    icon: FiBox,
    description: 'Perspektywa izometryczna pokazująca głębię i wysokość pomieszczeń. Pozwala na lepsze zrozumienie przestrzeni i bryły budynku.',
    prompt: `Generate a photorealistic 3D isometric architectural visualization based on the provided 2D floor plan.
The view should be at a 45-degree angle (isometric/axonometric perspective) to show wall heights and spatial depth.
Walls should be rendered as 3D solids with realistic thickness.
Interior lighting should be bright and airy, with soft shadows cast by the 3D walls.
Apply high-quality textures: polished hardwood floors, sleek kitchen surfaces, and soft fabrics on furniture.
Maintain exact furniture placement from the 2D layout.
Style: modern 3D floor plan, architectural isometric render, clean, professional, high detail.`
  },
  catalog: {
    id: 'catalog',
    label: 'Katalogowy Premium',
    icon: FiLayers,
    description: 'Styl studyjny, czysty i jasny, stosowany w profesjonalnych folderach deweloperskich i katalogach wnętrzarskich.',
    prompt: `Create a premium interior catalog-style orthographic 3D rendering based on the provided 2D floor plan image.
Maintain exact geometry fidelity: all walls, doors, windows, and furniture must match the 2D plan precisely.
Use a neutral orthographic top-down camera with no perspective distortion.
Apply soft studio-style lighting, balanced exposure, gentle shadows, and clean reflections.
Floors should feature elegant materials (oak wood, polished stone, ceramic tiles), evenly lit.
Walls in light neutral tones with minimal texture.
Style: premium interior catalog, clean, modern, bright, elegant, high realism.`
  },
  developer: {
    id: 'developer',
    label: 'Marketingowy Deweloperski',
    icon: FiSun,
    description: 'Ciepły i zachęcający styl marketingowy, skupiony na estetyce i przyjaznym odbiorze przez klienta końcowego.',
    prompt: `Produce a high-quality real estate developer-style orthographic 3D rendering from the provided 2D floor plan image.
Accurately translate all architectural elements and furniture into realistic 3D forms.
Use a perfectly vertical orthographic top-down camera with no perspective distortion.
Apply warm, inviting lighting with soft shadows and balanced global illumination (golden hour feel).
Floors should look realistic and appealing (warm wood, modern tiles).
Furniture realistic and welcoming, strictly based on the plan’s layout.
Style: professional real estate marketing render, inviting, clean, high detail, photorealistic.`
  },
  technical_blueprint: {
    id: 'technical_blueprint',
    label: 'Blueprint 3D / Techniczny',
    icon: FiGrid,
    description: 'Styl techniczny łączący rysunek CAD z nowoczesnym renderingiem 3D. Podkreśla wymiary i strukturę projektu.',
    prompt: `Generate a technical 3D blueprint style visualization from the provided 2D floor plan.
The render should have a clean, technical look with white or blueprint-blue background.
Walls should be extruded in 3D but with a semi-transparent or technical wireframe overlay.
Include subtle grid lines and technical annotations style if possible.
Lighting should be flat or minimal to emphasize lines and geometry.
Materials should be simple and monochromatic to highlight the architectural structure.
Style: technical architectural drawing, 3D blueprint, CAD visualization, clean lines.`
  },
  sketch_artistic: {
    id: 'sketch_artistic',
    label: 'Szkic Artystyczny',
    icon: FiPenTool,
    description: 'Artystyczna wizualizacja w formie odręcznego szkicu z delikatnym kolorem. Idealna na wczesnym etapie koncepcyjnym.',
    prompt: `Generate an artistic hand-drawn sketch visualization based on the provided 2D floor plan.
The style should resemble a professional architect's watercolor and ink sketch.
Visible pencil or ink lines with slight imperfections to give a handcrafted feel.
Soft watercolor washes for floors and furniture, staying mostly within the lines.
Top-down orthographic view.
Shadows should be rendered as cross-hatching or soft gray washes.
Style: architectural sketch, watercolor and ink, artistic concept, hand-drawn look.`
  }
};

const ExportDialog = ({ isOpen, onClose, onExport }) => {
  const [format, setFormat] = useState('png');
  const [quality, setQuality] = useState(100);
  const [resolution, setResolution] = useState(2);
  const [renderStyle, setRenderStyle] = useState('photorealistic');
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const url = exportManager.getThumbnailDataUrl(800);
      setPreviewUrl(url);
    }
  }, [isOpen]);

  const formatOptions = [
    { value: 'png', label: 'Obraz PNG', description: 'Wysoka jakość raster', icon: HiOutlinePhotograph, color: 'text-blue-600 bg-blue-50' },
    { value: 'jpg', label: 'Obraz JPG', description: 'Standardowy raster', icon: HiOutlinePhotograph, color: 'text-emerald-600 bg-emerald-50' },
    { value: 'render', label: 'Rendering AI', description: 'Wizualizacja 3D', icon: FiCamera, color: 'text-indigo-600 bg-indigo-50' },
    { value: 'json', label: 'Plik JSON', description: 'Kopia zapasowa projektu', icon: FiFileText, color: 'text-gray-500 bg-gray-50', isBackup: true }
  ];

  const resolutionOptions = [
    { value: 1, label: '1x (72 DPI)', description: 'Szybki podgląd' },
    { value: 2, label: '2x (144 DPI)', description: 'Druk i prezentacja' },
    { value: 3, label: '3x (300 DPI)', description: 'Najwyższa ostrość' }
  ];

  const handleExport = async () => {
    setIsExporting(true);
    setError('');
    setCopySuccess(false);
    
    try {
      if (format === 'render') {
        await navigator.clipboard.writeText(RENDER_STYLES[renderStyle].prompt);
        setCopySuccess(true);
        await exportManager.exportToImage('png', 1, 2);
        setTimeout(() => {
           onClose();
        }, 1500);
      } else if (format === 'json') {
        onExport({
          format,
          quality: quality / 100,
          resolution
        });
        onClose();
      } else {
        await exportManager.exportToImage(
          format, 
          quality / 100, 
          resolution
        );
        onClose();
      }
    } catch (error) {
      console.error('Export error:', error);
      setError(error.message || 'Błąd podczas eksportowania');
    } finally {
      if (format !== 'render') {
        setIsExporting(false);
      } else {
        setTimeout(() => setIsExporting(false), 1500);
      }
    }
  };

  const copyPromptOnly = async () => {
      try {
          await navigator.clipboard.writeText(RENDER_STYLES[renderStyle].prompt);
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
          console.error('Failed to copy', err);
      }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200">
              <SafeIcon icon={FiDownload} className="text-white text-2xl" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Eksportuj Projekt</h2>
              <p className="text-sm text-gray-500">Wybierz format i styl wizualizacji rzutu</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-400 hover:text-gray-600"
          >
            <SafeIcon icon={FiX} className="text-2xl" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar - Formats */}
          <div className="w-1/3 border-r border-gray-100 p-6 space-y-4 bg-gray-50/20 overflow-y-auto">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Format wyjściowy</h3>
            <div className="space-y-3">
              {formatOptions.map((option, index) => (
                <React.Fragment key={option.value}>
                  {option.isBackup && <div className="pt-4 mt-4 border-t border-gray-200"></div>}
                  <button 
                    onClick={() => setFormat(option.value)}
                    className={clsx(
                      "w-full p-4 rounded-xl border-2 transition-all flex items-start gap-4 text-left group",
                      format === option.value 
                        ? "border-indigo-600 bg-white shadow-md" 
                        : "border-transparent bg-white hover:border-gray-200 hover:shadow-sm"
                    )}
                  >
                    <div className={clsx("p-2 rounded-lg transition-transform group-hover:scale-110", option.color)}>
                      <SafeIcon icon={option.icon} className="text-xl" />
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-gray-900 flex items-center justify-between">
                        {option.label}
                        {format === option.value && <SafeIcon icon={FiCheck} className="text-indigo-600" />}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{option.description}</div>
                    </div>
                  </button>
                </React.Fragment>
              ))}
            </div>

            {format !== 'render' && format !== 'json' && (
              <div className="pt-6 space-y-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Ustawienia obrazu</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-700">Rozdzielczość</label>
                    <div className="grid grid-cols-1 gap-2">
                      {resolutionOptions.map((opt) => (
                        <button 
                          key={opt.value}
                          onClick={() => setResolution(opt.value)}
                          className={clsx(
                            "p-3 rounded-lg border text-left flex flex-col transition-all",
                            resolution === opt.value ? "border-indigo-600 bg-indigo-50" : "border-gray-200 hover:bg-gray-50"
                          )}
                        >
                          <span className="text-sm font-bold text-gray-900">{opt.label}</span>
                          <span className="text-[10px] text-gray-500">{opt.description}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main Area - Styles or JSON Info */}
          <div className="flex-1 p-8 overflow-y-auto">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-6">
                {error}
              </div>
            )}

            {format === 'render' ? (
              <div className="space-y-8 animate-in slide-in-from-right-4 duration-300">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-6">Wybierz Styl Wizualizacji AI</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.values(RENDER_STYLES).map((style) => (
                      <button
                        key={style.id}
                        onClick={() => setRenderStyle(style.id)}
                        className={clsx(
                          "p-5 rounded-2xl border-2 transition-all text-left flex flex-col gap-3 group relative overflow-hidden",
                          renderStyle === style.id
                            ? "border-indigo-600 bg-indigo-50/30 ring-1 ring-indigo-600 shadow-sm"
                            : "border-gray-100 hover:border-gray-300 hover:bg-gray-50"
                        )}
                      >
                        <div className="flex items-center gap-3">
                           <div className={clsx(
                             "w-10 h-10 rounded-lg flex items-center justify-center transition-colors",
                             renderStyle === style.id ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-500"
                           )}>
                             <SafeIcon icon={style.icon} className="text-xl" />
                           </div>
                           <span className={clsx("font-bold text-sm", renderStyle === style.id ? "text-indigo-900" : "text-gray-900")}>{style.label}</span>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed min-h-[40px]">{style.description}</p>
                        {renderStyle === style.id && (
                          <div className="absolute top-2 right-2">
                             <SafeIcon icon={FiCheck} className="text-indigo-600" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-100 space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-bold text-gray-900">Opis (Prompt) dla narzędzi AI</label>
                      <button 
                        onClick={copyPromptOnly}
                        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-50 transition-colors shadow-sm"
                      >
                         <SafeIcon icon={copySuccess ? FiCheck : FiCopy} className={clsx(copySuccess ? "text-green-600" : "text-gray-400")} />
                         {copySuccess ? 'Skopiowano!' : 'Kopiuj sam tekst'}
                      </button>
                    </div>
                    <div className="p-4 bg-gray-900 rounded-xl shadow-inner border border-gray-800">
                        <p className="text-xs font-mono text-gray-300 leading-relaxed whitespace-pre-wrap selection:bg-indigo-500/30">
                          {RENDER_STYLES[renderStyle].prompt}
                        </p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 flex gap-3">
                      <SafeIcon icon={FiBox} className="text-amber-600 text-lg mt-0.5 shrink-0" />
                      <div className="text-xs text-amber-800 leading-relaxed">
                        <strong>Jak to działa?</strong> Kliknij przycisk eksportu poniżej. System skopiuje powyższy opis do schowka i pobierze Twój rzut jako PNG. Wklej opis w Midjourney, DALL-E lub Stable Diffusion i dołącz pobrany obraz jako referencję.
                      </div>
                    </div>
                </div>
              </div>
            ) : format === 'json' ? (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-6 max-w-sm mx-auto animate-in fade-in duration-500">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiFileText} className="text-gray-300 text-5xl" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Eksport do JSON</h3>
                  <p className="text-sm text-gray-500 mt-2">
                    Ten format służy do tworzenia kopii zapasowych. Możesz go później zaimportować, aby kontynuować edycję projektu. Nie jest to format graficzny.
                  </p>
                </div>
                <div className="w-full p-4 bg-blue-50 rounded-xl border border-blue-100 text-left">
                  <div className="flex items-center gap-2 text-blue-700 font-bold text-xs uppercase tracking-wider mb-2">
                    <SafeIcon icon={FiCpu} /> Zawartość pliku
                  </div>
                  <ul className="text-[11px] text-blue-600 space-y-1 font-medium">
                    <li>• Geometria ścian i pomieszczeń</li>
                    <li>• Rozmieszczenie mebli i obiektów</li>
                    <li>• Wszystkie warstwy i ustawienia</li>
                    <li>• Historia zmian (opcjonalnie)</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="space-y-8 animate-in slide-in-from-right-4 duration-300">
                <h3 className="text-lg font-bold text-gray-900">Aktualny Podgląd Rzutu</h3>
                <div className="aspect-video bg-white rounded-2xl border border-gray-200 shadow-inner overflow-hidden flex items-center justify-center relative group">
                   {previewUrl ? (
                     <img 
                       src={previewUrl} 
                       alt="Podgląd rzutu" 
                       className="max-w-full max-h-full object-contain p-4"
                     />
                   ) : (
                     <div className="flex flex-col items-center gap-3 text-gray-400">
                        <SafeIcon icon={HiOutlinePhotograph} className="text-6xl opacity-20" />
                        <p className="text-sm font-medium">Generowanie podglądu...</p>
                     </div>
                   )}
                   <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-white text-[10px] font-medium text-center">Widok wygenerowany na podstawie aktualnego obszaru roboczego</p>
                   </div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Format</label>
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 font-bold text-gray-700 uppercase tracking-widest">
                      {format}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Mnożnik Rozmiaru</label>
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 font-bold text-gray-700">
                      {resolution}x (Wysoka Jakość)
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100 flex gap-3">
                   <SafeIcon icon={FiDownload} className="text-indigo-600 text-lg mt-0.5 shrink-0" />
                   <p className="text-xs text-indigo-800 leading-relaxed">
                     Obraz zostanie wygenerowany bezpośrednio w przeglądarce i automatycznie pobrany na Twój dysk w najwyższej możliwej jakości dla wybranej rozdzielczości.
                   </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50/80">
          <div className="flex items-center gap-2 text-gray-400">
             <SafeIcon icon={FiCheck} className="text-indigo-500" />
             <span className="text-xs font-medium">Wszystkie ustawienia gotowe</span>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={onClose}
              className="px-6 py-2.5 text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors"
              disabled={isExporting}
            >
              Anuluj
            </button>
            <button 
              onClick={handleExport}
              disabled={isExporting}
              className={clsx(
                  "px-8 py-3 rounded-xl transition-all flex items-center gap-3 font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-95",
                  isExporting && format === 'render' ? "bg-green-600 hover:bg-green-700 text-white shadow-green-200" : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-200"
              )}
            >
              <SafeIcon icon={format === 'render' ? FiCopy : FiDownload} className="text-lg" />
              {isExporting 
                  ? (format === 'render' ? 'Skopiowano!' : 'Eksportowanie...') 
                  : (format === 'json' ? 'Pobierz Plik JSON' : (format === 'render' ? 'Kopiuj i Pobierz PNG' : 'Rozpocznij Eksport'))
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportDialog;